import React from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Palette } from "lucide-react";

const THEMES = [
  { id: "indigo", name: "Modern Indigo", desc: "The premium dark-heavy SaaS look. Deep navy with vibrant indigo & violet accents.", swatch: ["#4F46E5", "#8B5CF6", "#0B0F19"] },
  { id: "green", name: "Fresh Green", desc: "Warm, natural, calm. Emerald & teal on deep forest background.", swatch: ["#34D399", "#2DD4BF", "#022C22"] },
  { id: "mono", name: "Neutral Mono", desc: "Clean & minimal. Black, white and grey — light. Perfect for print & focus.", swatch: ["#171717", "#737373", "#FAFAFA"] },
  { id: "mono-dark", name: "Neutral Dark", desc: "Editorial dark grayscale. Vercel/Linear vibes, monochrome dark with a soft white primary.", swatch: ["#FAFAFA", "#A3A3A3", "#0D0D0D"] },
];

const LANGS = [
  { id: "en", label: "English" },
  { id: "af", label: "Afrikaans (placeholder)" },
  { id: "zu", label: "isiZulu (placeholder)" },
];

export default function SettingsPage() {
  const { user, updateMe } = useAuth();
  const { theme, setTheme } = useTheme();

  const save = async (patch) => {
    await updateMe(patch);
    toast.success("Saved");
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Preferences</div>
        <h1 className="text-4xl font-bold tracking-tight">Settings</h1>
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Palette className="h-4 w-4 text-primary" /> Theme</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-4">
            {THEMES.map((t) => (
              <button
                key={t.id}
                onClick={() => { setTheme(t.id); save({ theme: t.id }); }}
                data-testid={`settings-theme-${t.id}`}
                className={`text-left p-4 rounded-2xl border transition-all lift ${theme === t.id ? "border-primary bg-primary/5" : "border-border bg-card"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">{t.name}</div>
                  {theme === t.id && <Check className="h-4 w-4 text-primary" />}
                </div>
                <div className="mt-2 text-xs text-muted-foreground leading-relaxed">{t.desc}</div>
                <div className="mt-3 flex -space-x-1">
                  {t.swatch.map((c, i) => (
                    <span key={i} className="h-5 w-5 rounded-full border border-border" style={{ background: c }} />
                  ))}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base">Profile</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Full name</Label>
            <Input defaultValue={user?.name} onBlur={(e) => save({ name: e.target.value })} className="rounded-2xl mt-1.5" data-testid="settings-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
            <Input defaultValue={user?.phone || ""} onBlur={(e) => save({ phone: e.target.value })} className="rounded-2xl mt-1.5" data-testid="settings-phone" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Language</Label>
            <Select defaultValue={user?.language || "en"} onValueChange={(v) => save({ language: v })}>
              <SelectTrigger className="rounded-2xl mt-1.5" data-testid="settings-language"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGS.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Role</Label>
            <div className="mt-1.5"><Badge className="rounded-full capitalize">{user?.role}</Badge></div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-dashed">
        <CardContent className="p-4 text-xs text-muted-foreground">
          <strong className="text-foreground">Mocked features:</strong> Paystack payments, Resend email invites, OTP delivery. All work end-to-end in the UI. Add real API keys in <code>backend/.env</code> to go live.
        </CardContent>
      </Card>
    </div>
  );
}
